import { useState } from 'react';
import { useT } from '../../i18n';
import { useNotificationSettings, useUpdateNotificationSettings } from '../../data/queries';
import { ensurePushSubscription, pushSupported } from '../../push';
import type { NotificationFrequency } from '../../data/dto';

const FREQUENCIES: NotificationFrequency[] = ['immediate', 'daily', 'weekly'];

/**
 * Settings section: per-baby notification preferences. Each baby the user is
 * connected to gets an on/off toggle plus a frequency choice (immediate push
 * per event, or one daily/weekly digest). Turning any baby on also asks the
 * browser for push permission and registers this device.
 */
export function NotificationSettings() {
  const t = useT();
  const settingsQ = useNotificationSettings();
  const update = useUpdateNotificationSettings();
  const [pushHint, setPushHint] = useState<string | null>(null);

  const rows = settingsQ.data?.settings ?? [];
  if (settingsQ.isLoading || rows.length === 0) return null;

  const onToggle = async (babyId: string, enabled: boolean) => {
    update.mutate({ babyId, enabled });
    if (enabled && pushSupported()) {
      const result = await ensurePushSubscription();
      if (result === 'denied') {
        setPushHint(t(
          'notifications.pushDenied',
          'Browser notifications are blocked for this site — updates will still appear in the app.',
        ));
      } else if (result === 'subscribed') {
        setPushHint(null);
      }
    }
  };

  const freqLabel: Record<NotificationFrequency, string> = {
    immediate: t('notifications.freq.immediate', 'Immediate'),
    daily:     t('notifications.freq.daily', 'Daily'),
    weekly:    t('notifications.freq.weekly', 'Weekly'),
  };

  return (
    <section>
      <h2 className="font-serif text-xl tracking-tight text-ink mb-1">
        {t('notifications.settings.title', 'Notifications')}
      </h2>
      <p className="text-[13px] text-ink-2 leading-relaxed mb-4">
        {t(
          'notifications.settings.desc',
          'Get notified when others add photos, milestones and more. Choose per baby whether you want every update right away or a daily/weekly summary.',
        )}
      </p>
      <div className="flex flex-col gap-2">
        {rows.map((row) => (
          <div key={row.baby_id} className="overflow-hidden rounded-xl border border-line bg-surface">
            <div className="flex items-center justify-between px-4 py-3.5">
              <span className="text-[14.5px] font-medium text-ink">{row.baby_name}</span>
              {/* Allow-notifications toggle */}
              <button
                role="switch"
                aria-checked={row.enabled}
                aria-label={t('notifications.settings.allow', 'Allow notifications')}
                onClick={() => onToggle(row.baby_id, !row.enabled)}
                disabled={update.isPending}
                className={[
                  'relative h-7 w-12 rounded-full transition-colors',
                  row.enabled ? 'bg-accent' : 'bg-grey-soft',
                ].join(' ')}
              >
                <span
                  className={[
                    'absolute top-0.5 size-6 rounded-full bg-surface shadow-[0_1px_3px_rgba(33,28,21,0.25)] transition-all',
                    row.enabled ? 'left-[22px]' : 'left-0.5',
                  ].join(' ')}
                />
              </button>
            </div>
            {row.enabled && (
              <div className="flex items-center justify-between border-t border-hairline px-4 py-3">
                <span className="text-[13px] text-ink-2">
                  {t('notifications.settings.frequency', 'Frequency')}
                </span>
                <div className="flex rounded-lg bg-grey-soft p-0.5">
                  {FREQUENCIES.map((f) => {
                    const on = row.frequency === f;
                    return (
                      <button
                        key={f}
                        onClick={() => update.mutate({ babyId: row.baby_id, frequency: f })}
                        disabled={update.isPending}
                        className={[
                          'inline-flex items-center px-3 py-1.5 text-[12px] rounded-md transition-all',
                          on
                            ? 'bg-surface text-ink font-semibold shadow-[0_1px_2px_rgba(33,28,21,0.08)]'
                            : 'bg-transparent text-ink-2 font-medium',
                        ].join(' ')}
                      >
                        {freqLabel[f]}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {pushHint && <p className="mt-2 text-[12.5px] leading-relaxed text-ink-3">{pushHint}</p>}
    </section>
  );
}
